import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Box, Typography, TextField, Chip, Button } from "@mui/material";
import { IoIosSearch, IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { SearchResults } from "../components/features/SearchBar";
import { PageContainer } from "../components";
import { SearchResult, SearchResultsResponse } from "../components/features/SearchBar/SearchResults";
import BuscasService from "../services/buscas.service";

const SearchResultsPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const query = searchParams.get("q") || "";

    const [searchResults, setSearchResults] = useState<SearchResultsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState<"titulo" | "tipo" | "">(""); // Padrão: relevância
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    // Refs for cleanup and request cancellation
    const abortControllerRef = useRef<AbortController | null>(null);
    const buscasServiceRef = useRef<BuscasService>(new BuscasService());

    // Tipos disponíveis - static to prevent re-creation
    const availableTypes = React.useMemo(() => [
        { value: "ARTIGO", label: "Artigo" },
        { value: "CURSO", label: "Curso" },
        { value: "AULA", label: "Aula" },
        { value: "VIDEO", label: "Vídeo" },
        { value: "TOPICO", label: "Notícia" },
    ], []);

    // Função para executar busca com cancelamento de requisições anteriores
    const performSearch = useCallback(
        async (searchQuery: string, page: number = 1) => {
            if (!searchQuery.trim()) return;

            // Cancel previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();

            setLoading(true);
            try {
                // Usar tipos selecionados ou todos se nenhum selecionado
                const typesToUse = selectedTypes.length > 0 ? selectedTypes : ["ARTIGO", "CURSO", "AULA", "VIDEO", "TOPICO"];

                // Criar string de ordenação
                let ordering = "";
                if (sortBy !== "") {
                    ordering = sortDirection === "desc" ? `-${sortBy}` : sortBy;
                } else {
                    ordering = ""; // padrão: relevância (sem ordenação específica)
                }

                const results = await buscasServiceRef.current.searchComplete({
                    q: searchQuery,
                    tipos: typesToUse,
                    ordering,
                    page,
                });

                // Only update state if request wasn't cancelled
                if (!abortControllerRef.current?.signal.aborted) {
                    // Se não há ordenação específica, ordenar por relevância (search_score)
                    if (ordering === "" && results.results) {
                        results.results = results.results.sort((a: SearchResult, b: SearchResult) => {
                            const scoreA = a.search_score || 0;
                            const scoreB = b.search_score || 0;
                            return scoreB - scoreA; // Ordem decrescente (maior score primeiro)
                        });
                    }

                    setSearchResults(results);
                    setCurrentPage(page);
                }
            } catch (error) {
                // Only handle error if request wasn't cancelled
                if (!abortControllerRef.current?.signal.aborted) {
                    console.error("Erro na busca:", error);
                    setSearchResults({
                        error: true,
                        message: "Erro ao realizar busca",
                        count: 0,
                        next: null,
                        previous: null,
                        results: [],
                    });
                }
            } finally {
                if (!abortControllerRef.current?.signal.aborted) {
                    setLoading(false);
                }
            }
        },
        [selectedTypes, sortBy, sortDirection]
    );

    // Executar busca quando query ou filtros mudarem
    useEffect(() => {
        if (!query.trim()) {
            navigate("/cursos", { replace: true });
            return;
        }

        performSearch(query, 1);
    }, [query, selectedTypes, sortBy, sortDirection, navigate, performSearch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleItemClick = (item: SearchResult) => {
        // Navegação baseada no tipo do item (mesma lógica do Header)
        switch (item.tipo) {
            case "ARTIGO":
                navigate(`/artigo/${item.ref1_slug || item.id}`);
                break;
            case "CURSO":
                navigate(`/cursos/${item.ref1_slug || item.id}`);
                break;
            case "AULA":
                if (item.ref2_slug) {
                    navigate(`/cursos/${item.ref2_slug}/${item.id}`);
                } else {
                    navigate(`/aulas/${item.id}`);
                }
                break;
            case "VIDEO":
                if (item.ref2_slug) {
                    navigate(`/serie/${item.ref2_slug}/${item.id}`);
                } else {
                    navigate(`/videos/${item.id}`);
                }
                break;
            case "TOPICO":
                navigate(`/noticias/${item.id}`);
                break;
            default:
                console.log("Tipo não reconhecido:", item.tipo);
        }
    };

    const handlePageChange = useCallback(
        async (page: number) => {
            await performSearch(query, page);
        },
        [query, performSearch]
    );

    // Função para toggle de tipo - removendo setTimeout para evitar race conditions
    const handleTypeToggle = useCallback(
        (type: string) => {
            setSelectedTypes((prev) => {
                const newTypes = prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type];
                return newTypes;
            });
        },
        []
    );

    // Função para mudança de ordenação - removendo setTimeout
    const handleSortChange = useCallback(
        (newSortBy: "titulo" | "tipo" | "") => {
            if (newSortBy === "") {
                // Relevância - limpar ordenação
                setSortBy("");
                setSortDirection("asc");
            } else if (sortBy === newSortBy) {
                // Se já está ordenando por este campo, inverter direção
                setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            } else {
                // Novo campo de ordenação
                setSortBy(newSortBy);
                setSortDirection("asc");
            }
        },
        [sortBy]
    );

    if (!query.trim()) {
        return null; // Componente está redirecionando
    }

    return (
        <PageContainer pageTitle={`Resultados da busca: "${query}"`} showUserAvatar={true} showSchoolLogo={true} showMenu={true}>
            <Container maxWidth="lg">
                <Box py={3}>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Digite para buscar cursos, artigos, vídeos..."
                            defaultValue={query}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    const target = e.target as HTMLInputElement;
                                    if (target.value.trim()) {
                                        navigate(`/buscar?q=${encodeURIComponent(target.value.trim())}`);
                                    }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                                        <IoIosSearch size={20} style={{ color: "#9e9e9e", transition: "color 0.3s ease" }} />
                                    </Box>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "white",
                                    borderRadius: "50px", // Bordas totalmente arredondadas
                                    height: "48px",
                                    fontSize: "1rem",
                                    border: "2px solid #e0e0e0",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        borderColor: "#bdbdbd",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                                        transform: "translateY(-1px)",
                                    },
                                    "&.Mui-focused": {
                                        borderColor: "#013a7b",
                                        boxShadow: "0 4px 16px rgba(1, 58, 123, 0.25)",
                                        transform: "translateY(-1px)",
                                        "& svg": {
                                            color: "#013a7b !important",
                                        },
                                    },
                                    "& fieldset": {
                                        border: "none", // Remove borda padrão do MUI
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Filtros por Tags e Ordenação na mesma linha */}
                    {searchResults && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", mb: 2 }}>
                                {/* Filtros por Tipo */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#666", fontWeight: 600 }}>
                                        Filtrar por tipo:
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                        {availableTypes.map((type) => (
                                            <Chip
                                                key={type.value}
                                                label={type.label}
                                                onClick={() => handleTypeToggle(type.value)}
                                                variant={selectedTypes.includes(type.value) ? "filled" : "outlined"}
                                                sx={{
                                                    ...(selectedTypes.includes(type.value) ?
                                                        { backgroundColor: '#013a7b', color: 'white', '&:hover': { backgroundColor: '#013a7b', transform: "translateY(-1px)", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)" } } :
                                                        { borderColor: '#013a7b', color: '#013a7b', '&:hover': { borderColor: '#013a7b', backgroundColor: 'rgba(1, 58, 123, 0.04)', transform: "translateY(-1px)", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)" } }
                                                    ),
                                                    borderRadius: "16px",
                                                    fontWeight: 500,
                                                    transition: "all 0.2s ease",
                                                    height: "32px",
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Ordenação */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#666", fontWeight: 600 }}>
                                        Ordenar por:
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        {/* Botão Relevância */}
                                        <Button
                                            variant={!sortBy ? "contained" : "outlined"}
                                            onClick={() => handleSortChange("")}
                                            sx={{
                                                borderRadius: "20px",
                                                textTransform: "none",
                                                fontWeight: 600,
                                                minWidth: "80px",
                                                ...(!sortBy ? { backgroundColor: '#013a7b', '&:hover': { backgroundColor: '#013a7b' } } : { borderColor: '#013a7b', color: '#013a7b', '&:hover': { borderColor: '#013a7b', backgroundColor: 'rgba(1, 58, 123, 0.04)' } })
                                            }}>
                                            Relevância
                                        </Button>

                                        {/* Botão Título */}
                                        <Button
                                            variant={sortBy === "titulo" ? "contained" : "outlined"}
                                            onClick={() => handleSortChange("titulo")}
                                            endIcon={sortBy === "titulo" ? sortDirection === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown /> : null}
                                            sx={{
                                                borderRadius: "20px",
                                                textTransform: "none",
                                                fontWeight: 600,
                                                minWidth: "80px",
                                                ...(sortBy === "titulo" ? { backgroundColor: '#013a7b', '&:hover': { backgroundColor: '#013a7b' } } : { borderColor: '#013a7b', color: '#013a7b', '&:hover': { borderColor: '#013a7b', backgroundColor: 'rgba(1, 58, 123, 0.04)' } })
                                            }}>
                                            Título
                                        </Button>

                                        {/* Botão Tipo */}
                                        <Button
                                            variant={sortBy === "tipo" ? "contained" : "outlined"}
                                            onClick={() => handleSortChange("tipo")}
                                            endIcon={sortBy === "tipo" ? sortDirection === "asc" ? <IoIosArrowUp /> : <IoIosArrowDown /> : null}
                                            sx={{
                                                borderRadius: "20px",
                                                textTransform: "none",
                                                fontWeight: 600,
                                                minWidth: "80px",
                                                ...(sortBy === "tipo" ? { backgroundColor: '#013a7b', '&:hover': { backgroundColor: '#013a7b' } } : { borderColor: '#013a7b', color: '#013a7b', '&:hover': { borderColor: '#013a7b', backgroundColor: 'rgba(1, 58, 123, 0.04)' } })
                                            }}>
                                            Tipo
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <SearchResults
                        results={searchResults}
                        loading={loading}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        onItemClick={handleItemClick}
                    />
                </Box>
            </Container>
        </PageContainer>
    );
};

export default SearchResultsPage;
